// PO-GST-01 · P2-D1-C · GST & Terms UI. Displays backend-authoritative results only (no browser tax maths).
import { useEffect, useRef, useState } from "react";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { CHARGE_TREATMENTS, CHARGE_TYPES, NON_TAXABLE_CLASSES, STATE_CODES } from "@/lib/poGst";
import { SUPPLIER_CLASS_FINDING, SUPPLIER_CLASS_GUIDANCE } from "@/lib/supplierGstClass";

const cell = "w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]";
const lbl = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";
const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const PREVIEW_DEBOUNCE_MS = 450;

// Debounced (typing pause) + latest-only: a response is applied only if no newer input/disable happened.
export function useGstPreview(payload, enabled) {
  const [state, setState] = useState({ data: null, loading: false, error: "" });
  const seq = useRef(0);
  const key = enabled ? JSON.stringify(payload) : "";
  useEffect(() => {
    const my = ++seq.current;   // any key change (incl. disable) invalidates in-flight responses
    if (!key) { setState({ data: null, loading: false, error: "" }); return undefined; }
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.post("/purchase-orders/gst-preview", JSON.parse(key))
        .then(({ data }) => my === seq.current && setState({ data, loading: false, error: "" }))
        .catch((e) => my === seq.current && setState({ data: null, loading: false, error: apiErrorMessage(e, "GST preview failed") }));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [key]);
  return state;
}

export function GstControlBadge({ po }) {
  if (po?.gst_control?.enabled === true) {
    const e = po.tax_snapshot;
    return (
      <span data-testid="po-gst-control-badge" className="inline-flex items-center px-2 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-200 text-[10px] font-mono uppercase tracking-widest">
        GST-Controlled{e ? ` · Snapshot #${e.seq} · Rev ${e.po_revision}` : ""}
      </span>
    );
  }
  return (
    <span data-testid="po-legacy-badge" title="Created before GST control — GST inputs are not editable and the PO is never auto-converted"
      className="inline-flex items-center px-2 py-0.5 rounded-sm border border-zinc-500/50 bg-zinc-500/10 text-zinc-300 text-[10px] font-mono uppercase tracking-widest">
      Legacy · No GST Control
    </span>
  );
}

export function GstRateCell({ line, onChange, source, testid, disabled = false }) {
  const typedZero = !line.zero_confirmed && line.tax_pct !== "" && line.tax_pct != null && Number(line.tax_pct) === 0;
  const set = (patch) => onChange({ ...patch, gst_rate_source: "edited", gst_dirty: true });
  return (
    <div className="min-w-[84px]">
      <input data-testid={`${testid}-gst`} type="number" step="0.01" min="0" max="100" placeholder="—"
        value={line.zero_confirmed ? "0" : (line.tax_pct ?? "")} disabled={disabled || line.zero_confirmed}
        onChange={(e) => set({ tax_pct: e.target.value, zero_confirmed: false })}
        className={`${cell} text-right font-mono disabled:opacity-60`} />
      <label className="mt-0.5 flex items-center justify-end gap-1 text-[9px] font-mono text-zinc-400 cursor-pointer" title="Explicitly confirm a 0% GST rate (distinct from blank / unresolved)">
        <input data-testid={`${testid}-zero-confirm`} type="checkbox" className="accent-[var(--accent)]"
          checked={!!line.zero_confirmed} disabled={disabled}
          onChange={(e) => set(e.target.checked ? { tax_pct: "0", zero_confirmed: true } : { tax_pct: "", zero_confirmed: false })} />
        Confirm 0%
      </label>
      {typedZero && <div data-testid={`${testid}-zero-hint`} className="text-[9px] text-amber-300 text-right">Tick to confirm 0%</div>}
      {source && <div data-testid={`${testid}-gst-source`} className="text-[9px] font-mono text-zinc-500 text-right uppercase">{source}</div>}
    </div>
  );
}

export function DiscountCells({ line, onChange, testid, disabled = false }) {
  return (
    <>
      <td className="px-2 py-1.5">
        <input data-testid={`${testid}-disc-pct`} type="number" step="0.01" min="0" max="100" placeholder="%"
          value={line.discount_pct ?? ""} disabled={disabled} onChange={(e) => onChange({ discount_pct: e.target.value })}
          className={`${cell} text-right font-mono w-16`} />
      </td>
      <td className="px-2 py-1.5">
        <input data-testid={`${testid}-disc-amt`} type="number" step="0.01" min="0" placeholder="₹"
          value={line.discount_amt ?? ""} disabled={disabled} onChange={(e) => onChange({ discount_amt: e.target.value })}
          className={`${cell} text-right font-mono w-20`} />
      </td>
    </>
  );
}

export function GstHeaderFields({ value, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <label className={lbl}>Place of Supply (Delivery State)</label>
        <select data-testid="po-gst-pos" value={value.place_of_supply_state_code || ""}
          onChange={(e) => onChange({ place_of_supply_state_code: e.target.value })} className={`${cell} h-9`}>
          <option value="">— Not a separate delivery site —</option>
          {STATE_CODES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs text-zinc-300 sm:pt-5 cursor-pointer">
        <input data-testid="po-gst-receiving" type="checkbox" className="accent-[var(--accent)]"
          checked={value.company_is_receiving_location === true}
          onChange={(e) => onChange({ company_is_receiving_location: e.target.checked })} />
        Goods received at our company location
      </label>
      <label className="flex items-center gap-2 text-xs text-zinc-300 sm:pt-5 cursor-pointer">
        <input data-testid="po-gst-rcm" type="checkbox" className="accent-[var(--accent)]"
          checked={value.reverse_charge === true}
          onChange={(e) => onChange({ reverse_charge: e.target.checked })} />
        Reverse charge applies
      </label>
    </div>
  );
}

function ChargeRow({ c, i, set, remove }) {
  return (
    <div data-testid={`po-charge-${i}`} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-start border-t border-[var(--border)] pt-2">
      <select data-testid={`po-charge-${i}-type`} value={c.code} className={`${cell} sm:col-span-2`}
        onChange={(e) => set({ code: e.target.value, label: CHARGE_TYPES.find((t) => t.code === e.target.value)?.label || "" })}>
        {CHARGE_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
      </select>
      <input data-testid={`po-charge-${i}-label`} value={c.label || ""} placeholder="Label" maxLength={60}
        onChange={(e) => set({ label: e.target.value })} className={`${cell} sm:col-span-2`} />
      <input data-testid={`po-charge-${i}-amount`} type="number" step="0.01" min="0" placeholder="Amount ₹"
        value={c.amount ?? ""} onChange={(e) => set({ amount: e.target.value })} className={`${cell} text-right font-mono sm:col-span-2`} />
      <select data-testid={`po-charge-${i}-treatment`} value={c.treatment} onChange={(e) => set({ treatment: e.target.value })}
        className={`${cell} sm:col-span-2`}>
        {CHARGE_TREATMENTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      {c.treatment === "SEPARATE_SERVICE" && (<>
        <input data-testid={`po-charge-${i}-sac`} value={c.sac_code || ""} placeholder="SAC (99xxxx)" maxLength={6}
          onChange={(e) => set({ sac_code: e.target.value.replace(/\s+/g, "") })} className={`${cell} font-mono sm:col-span-2`} />
        <input data-testid={`po-charge-${i}-rate`} type="number" step="0.01" min="0" placeholder="GST %"
          value={c.gst_rate ?? ""} onChange={(e) => set({ gst_rate: e.target.value })} className={`${cell} text-right font-mono sm:col-span-1`} />
      </>)}
      {c.treatment === "NON_TAXABLE" && (<>
        <input data-testid={`po-charge-${i}-reason`} value={c.non_taxable_reason || ""} placeholder="Reason" maxLength={120}
          onChange={(e) => set({ non_taxable_reason: e.target.value })} className={`${cell} sm:col-span-2`} />
        <select data-testid={`po-charge-${i}-class`} value={c.non_taxable_classification || ""}
          onChange={(e) => set({ non_taxable_classification: e.target.value })} className={`${cell} sm:col-span-1`}>
          <option value="">Class…</option>
          {NON_TAXABLE_CLASSES.map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
        </select>
      </>)}
      {c.treatment === "INCLUDED_IN_GOODS" && <div className="text-[10px] text-zinc-500 sm:col-span-3 pt-2">Spread over goods lines, taxed at each line's rate</div>}
      <button type="button" data-testid={`po-charge-${i}-del`} onClick={remove} className="h-8 text-zinc-500 hover:text-red-400 justify-self-end sm:col-span-1">
        <TrashIcon size={12} weight="bold" />
      </button>
    </div>
  );
}

export function ChargesEditor({ charges, onChange }) {
  const list = charges || [];
  const set = (i, patch) => onChange(list.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...list, { code: "FREIGHT", label: "Freight", amount: "", treatment: "INCLUDED_IN_GOODS" }]);
  return (
    <div className="space-y-2" data-testid="po-charges">
      <div className="flex items-center justify-between">
        <span className={lbl}>Freight / Packing / Other Charges · {list.length}</span>
        <button type="button" data-testid="po-charge-add" onClick={add}
          className="h-7 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-[11px] rounded-sm flex items-center gap-1">
          <PlusIcon size={10} weight="bold" />Add Charge
        </button>
      </div>
      {list.map((c, i) => <ChargeRow key={i} c={c} i={i} set={(p) => set(i, p)} remove={() => onChange(list.filter((_, j) => j !== i))} />)}
    </div>
  );
}

const ROWS = [
  ["gross_total", "Gross value"], ["discount_total", "Line discounts"],
  ["goods_taxable_before_charges", "Goods value (PO total, excl. GST & charges)"],
  ["included_charges_total", "Charges included in goods"], ["service_charges_taxable", "Separate service charges"],
  ["taxable_total", "Taxable value"], ["cgst_total", "CGST"], ["sgst_total", "SGST"], ["igst_total", "IGST"],
  ["non_taxable_charges_total", "Non-taxable charges"], ["round_off", "Round off"],
];

function Findings({ items, testid }) {
  if (!items.length) return null;
  return (
    <ul data-testid={testid} className="space-y-1 mt-2">
      {items.map((f, i) => (
        <li key={i} className={`text-[11px] font-mono ${f.severity === "BLOCKING" ? "text-rose-300" : "text-amber-300"}`}>
          {f.severity === "BLOCKING" ? "■" : "▲"} {f.line_no ? `L${f.line_no} · ` : ""}{f.charge_code ? `${f.charge_code} · ` : ""}{f.message || f.code} <span className="text-zinc-600">[{f.code}]</span>
          {f.code === SUPPLIER_CLASS_FINDING && <div data-testid={`${testid || "po-gst-findings"}-supplier-class-guidance`} className="text-sky-300 pl-4">{SUPPLIER_CLASS_GUIDANCE}</div>}
        </li>
      ))}
    </ul>
  );
}

export function GstResultPanel({ result, loading, error, idleText, prefix = "po-gst" }) {
  if (error) return <div data-testid={`${prefix}-preview-error`} className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/5 rounded-sm p-2">{error}</div>;
  if (!result) return <div data-testid={`${prefix}-preview-idle`} className="text-xs text-zinc-500">{loading ? "Calculating GST…" : idleText}</div>;
  const t = result.totals || {};
  const ok = result.tax_status === "COMPUTED";
  const cls = result.classification || {};
  return (
    <div data-testid={prefix === "po-gst" ? (result.kind === "saved" ? "po-gst-saved" : "po-gst-preview") : `${prefix}-panel`} className={`text-xs ${loading ? "opacity-60" : ""}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span data-testid={`${prefix}-status`} className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${ok ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
          Tax {result.tax_status}
        </span>
        <span data-testid={`${prefix}-source`} className="text-[10px] font-mono text-zinc-500 uppercase">
          {result.label ? result.label : result.kind === "saved" ? `Saved snapshot #${result.seq} · Rev ${result.revision}${result.current === false ? " · stale" : ""}` : `Preview · not saved · will be snapshot #${result.seq} · Rev ${result.revision}`}
        </span>
        {cls.tax_type && <span data-testid={`${prefix}-tax-type`} className="text-[10px] font-mono text-sky-300 uppercase">{cls.tax_type === "intra" ? "Intra-state · CGST + SGST" : "Inter-state · IGST"}</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
        {ROWS.map(([k, label]) => (
          <div key={k} className="flex justify-between border-b border-[var(--border)]/40 py-0.5">
            <span className="text-zinc-400">{label}</span>
            <span data-testid={`${prefix}-${k}`} className="font-mono tabular-nums text-zinc-200">{money(t[k])}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-[var(--border)]">
        <span className="uppercase tracking-widest text-[10px] text-zinc-400">Grand Total (incl. GST)</span>
        <span data-testid={`${prefix}-grand_total`} className="font-mono text-base text-white font-semibold">{t.grand_total == null ? "Unresolved" : money(t.grand_total)}</span>
      </div>
      <Findings items={result.blockers || []} testid={`${prefix}-save-blockers`} />
      <Findings items={result.findings || []} testid={`${prefix}-findings`} />
    </div>
  );
}

export function GstTermsSection({ value, onChange, result, loading, error, idleText, testid }) {
  return (
    <div data-testid={testid} className="border-t border-[var(--border)] pt-3 mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-3 min-w-0">
        <div className="overline">GST &amp; Terms</div>
        <GstHeaderFields value={value} onChange={onChange} />
        <ChargesEditor charges={value.charges} onChange={(charges) => onChange({ charges })} />
      </div>
      <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-0)]/40 min-w-0">
        <div className="overline mb-2">GST Calculation · Server Authoritative</div>
        <GstResultPanel result={result} loading={loading} error={error} idleText={idleText} />
      </div>
    </div>
  );
}
